import React, { useState, useEffect } from 'react';
import {useNavigate} from 'react-router-dom';import {useTranslation} from 'react-i18next';import {Check, Sparkles, ArrowLeft, Loader2, Crown, Users, ArrowUpRight, ArrowDownRight, RefreshCw, RotateCcw} from 'lucide-react';import {Button} from '@/components/ui/button';import {useSubscription} from '@/contexts/SubscriptionContext';import {toast} from 'sonner';import axios from 'axios';
import Header from '@/components/Header';
import {API_URL as API} from '@/utils/apiConfig';import {isTWA, isPlayBillingAvailable, purchaseWithPlayBilling} from '@/services/playBilling';import {getPlatform, getPaymentMethod, PLATFORM} from '@/services/platformDetection';import {purchaseWithApple, restoreApplePurchases, isAppleBridgeReady, setDespiaUserId} from '@/services/appleStoreKit';

import { useSafeBack } from '@/utils/useSafeBack';
const PLAN_FEATURES = {
  premium: [
    'plans.feat_unlimited_messages', 'plans.feat_all_themes', 'plans.feat_youtube',
    'plans.feat_file_upload', 'plans.feat_recap', 'plans.feat_push',
    'plans.feat_commentary', 'plans.feat_greek', 'plans.feat_gospel_lens',
    'plans.feat_devices', 'plans.feat_sync', 'plans.feat_priority',
  ],
  share: [
    'plans.feat_everything_premium', 'plans.feat_members', 'plans.feat_devices_per_person',
    'plans.feat_cross_platform', 'plans.feat_all_features', 'plans.feat_invite_link',
  ],
};

const TIER_ORDER = { free: 0, paid: 1, premium: 2, share: 3 };

const PLANS = [
  {
    key: 'free',
    nameKey: 'plans.free_name',
    price: '$0',
    priceCents: 0,
    period: '',
    icon: Users,
    accent: '#6b7280',
    billing: 'both',
    featureKeys: [
      'plans.feat_basic_messages', 'plans.feat_basic_themes', 'plans.feat_community',
    ],
  },
  {
    key: 'premium',
    nameKey: 'plans.premium_name',
    price: '$9.99',
    priceCents: 999,
    period: '/month',
    icon: Crown,
    accent: '#f59e0b',
    popular: true,
    billing: 'monthly',
    featureKeys: PLAN_FEATURES.premium,
  },
  {
    key: 'premium_annual',
    nameKey: 'plans.premium_name',
    price: '$83.99',
    priceCents: 8399,
    period: '/year',
    monthlyEquiv: '$7.00/mo',
    savings: 'Save 30%',
    icon: Crown,
    accent: '#f59e0b',
    popular: true,
    billing: 'annual',
    featureKeys: PLAN_FEATURES.premium,
  },
  {
    key: 'share',
    nameKey: 'plans.share_name',
    price: '$19.99',
    priceCents: 1999,
    period: '/month',
    icon: Users,
    accent: '#10b981',
    billing: 'monthly',
    featureKeys: PLAN_FEATURES.share,
  },
  {
    key: 'share_annual',
    nameKey: 'plans.share_name',
    price: '$167.99',
    priceCents: 16799,
    period: '/year',
    monthlyEquiv: '$14.00/mo',
    savings: 'Save 30%',
    icon: Users,
    accent: '#10b981',
    billing: 'annual',
    featureKeys: PLAN_FEATURES.share,
  },
];

function fmt(cents) {
  return `$${(Math.abs(cents) / 100).toFixed(2)}`;
}

export default function SubscriptionPlansPage({ user }) {
  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/home');
  const { t } = useTranslation();
  const { tier, isPaid, refreshStatus } = useSubscription();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [playBillingReady, setPlayBillingReady] = useState(false);
  const [appleBillingReady, setAppleBillingReady] = useState(false);
  const [restoringPurchases, setRestoringPurchases] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
  const detectedPlatform = getPlatform();
  const detectedPaymentMethod = getPaymentMethod();

  // Filter plans based on billing cycle toggle
  const visiblePlans = PLANS.filter(p => p.billing === 'both' || p.billing === billingCycle);

  // Check for native payment availability on mount
  useEffect(() => {
    async function detectPaymentCapabilities() {
      // Set user ID for Despia/RevenueCat purchases
      if (user?.user_id) {
        setDespiaUserId(user.user_id);
      }
      if (detectedPlatform === PLATFORM.ANDROID || isTWA()) {
        const ready = await isPlayBillingAvailable();
        setPlayBillingReady(ready);
      }
      if (detectedPlatform === PLATFORM.IOS) {
        setAppleBillingReady(isAppleBridgeReady());
      }
    }
    detectPaymentCapabilities();
  }, [detectedPlatform, user]);

  // Close preview on escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setPreview(null); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const handleSubscribe = async (planKey) => {
    setLoadingPlan(planKey);
    try {
      // Route to the correct payment flow based on detected platform
      if (playBillingReady) {
        // Android: Google Play Billing
        const result = await purchaseWithPlayBilling(planKey);
        if (result?.success) {
          toast.success(t('plans.subscription_activated', 'Subscription activated!'));
          refreshStatus();
          navigate('/app');
        }
      } else if (appleBillingReady) {
        // iOS/Android native via Despia + RevenueCat
        try {
          const result = await purchaseWithApple(planKey);
          if (result === null) {
            // User cancelled — no error
            return;
          }
          if (result?.success) {
            toast.success(t('plans.subscription_activated', 'Subscription activated!'));
            refreshStatus();
            navigate('/app');
          }
        } catch (nativeErr) {
          // If Despia signals fallback to Stripe (web purchase link not configured)
          if (nativeErr?.message === 'FALLBACK_TO_STRIPE') {
            const res = await axios.post(
              `${API}/payments/create-checkout`,
              { plan: planKey, origin_url: window.location.origin },
              { withCredentials: true }
            );
            if (res.data.url) {
              window.location.href = res.data.url;
            }
          } else {
            throw nativeErr;
          }
        }
      } else {
        // Web: Stripe checkout
        const res = await axios.post(
          `${API}/payments/create-checkout`,
          { plan: planKey, origin_url: window.location.origin },
          { withCredentials: true }
        );
        if (res.data.url) {
          window.location.href = res.data.url;
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t('plans.checkout_failed', 'Failed to start checkout. Please try again.');
      if (msg !== 'FALLBACK_TO_STRIPE') {
        toast.error(msg);
      }
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleRestorePurchases = async () => {
    setRestoringPurchases(true);
    try {
      const result = await restoreApplePurchases();
      if (result.restored) {
        toast.success(t('plans.purchases_restored', 'Purchases restored successfully!'));
        refreshStatus();
      } else {
        toast.info(result.message || t('plans.no_purchases_found', 'No previous purchases found.'));
      }
    } catch (err) {
      toast.error(err?.message || t('plans.restore_failed', 'Failed to restore purchases.'));
    } finally {
      setRestoringPurchases(false);
    }
  };

  const handlePreviewChange = async (planKey) => {
    setPreviewLoading(planKey);
    try {
      const res = await axios.post(
        `${API}/payments/preview-change`,
        { new_plan: planKey },
        { withCredentials: true }
      );
      setPreview({ ...res.data, targetPlan: planKey });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not preview plan change.';
      toast.error(msg);
    } finally {
      setPreviewLoading(null);
    }
  };

  const handleConfirmChange = async () => {
    if (!preview) return;
    setChangingPlan(true);
    try {
      await axios.post(
        `${API}/payments/change-plan`,
        { new_plan: preview.targetPlan },
        { withCredentials: true }
      );
      toast.success(t('plans.switched_to', { name: PLANS.find(p => p.key === preview.targetPlan) ? t(PLANS.find(p => p.key === preview.targetPlan).nameKey) : preview.targetPlan }));
      setPreview(null);
      await refreshStatus();
      window.dispatchEvent(new Event('subscription-changed'));
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to change plan.';
      toast.error(msg);
    } finally {
      setChangingPlan(false);
    }
  };

  const getPlanName = (planKey) => {
    const p = PLANS.find(pl => pl.key === planKey);
    return p ? t(p.nameKey) : planKey;
  };

  // currentPlanObj is unused — tier ordering uses TIER_ORDER directly

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)' }}>
      <Header user={user} />
      <div className="page-content max-w-4xl mx-auto px-4 py-8 pb-20 w-full" data-testid="subscription-plans-page">
        {/* Back button */}
        <button
          onClick={goBackSafe}
          className="flex items-center gap-2 mb-6 text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: 'var(--theme-textSecondary)' }}
          data-testid="plans-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> {t('plans.back')}
        </button>

        {/* Heading */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--theme-text)' }}>
            {isPaid ? t('plans.manage_plan') : t('plans.choose_plan')}
          </h1>
          <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
            {isPaid
              ? t('plans.manage_desc')
              : t('plans.choose_desc')}
          </p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-2">
          <div className="inline-flex rounded-full p-1" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
            <button
              onClick={() => setBillingCycle('monthly')}
              className="px-5 py-2 rounded-full text-sm font-medium transition-all"
              style={billingCycle === 'monthly'
                ? { background: 'var(--theme-accent)', color: '#fff' }
                : { color: 'var(--theme-textSecondary)' }}
              data-testid="billing-toggle-monthly"
            >
              {t('plans.monthly', 'Monthly')}
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className="px-5 py-2 rounded-full text-sm font-medium transition-all"
              style={billingCycle === 'annual'
                ? { background: 'var(--theme-accent)', color: '#fff' }
                : { color: 'var(--theme-textSecondary)' }}
              data-testid="billing-toggle-annual"
            >
              {t('plans.annual', 'Annual')}
              <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: billingCycle === 'annual' ? '#fff' : '#10b981' }}>
                {t('generated.subscriptionplanspage_save_30_5fae')}
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid gap-5 sm:grid-cols-3 max-w-4xl mx-auto">
          {visiblePlans.map((plan) => {
            // For annual plans, check if user's current tier matches (regardless of billing cycle)
            const planTier = plan.key.replace('_annual', '');
            const isCurrentPlan = tier === planTier || (tier === 'paid' && planTier === 'premium');
            const Icon = plan.icon;
            const currentTierOrder = TIER_ORDER[tier] ?? 0;
            const planTierOrder = TIER_ORDER[planTier] ?? 0;
            const isUpgrade = isPaid && planTierOrder > currentTierOrder;
            const isDowngrade = isPaid && planTierOrder < currentTierOrder;

            return (
              <div
                key={plan.key}
                className="relative rounded-3xl p-6 flex flex-col"
                style={{
                  background: 'var(--glass-bg)',
                  backdropFilter: 'blur(20px)',
                  border: isCurrentPlan
                    ? '2px solid var(--theme-accent)'
                    : plan.popular
                      ? `2px solid ${plan.accent}`
                      : '1px solid var(--glass-border)',
                }}
                data-testid={`plan-card-${plan.key}`}
              >
                {isCurrentPlan && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide"
                    style={{ background: 'var(--theme-accent)', color: '#fff' }}
                    data-testid="current-plan-badge"
                  >
                    {t('plans.current_plan')}
                  </div>
                )}
                {!isCurrentPlan && plan.popular && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide"
                    style={{ background: plan.accent, color: '#fff' }}
                    data-testid="popular-badge"
                  >
                    {t('plans.most_popular')}
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${plan.accent}22` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: plan.accent }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
                      {t(plan.nameKey)}
                    </h3>
                  </div>
                </div>
                <div className="mb-5">
                  <span className="text-3xl font-bold" style={{ color: 'var(--theme-text)' }}>
                    {plan.price}
                  </span>
                  <span className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
                    {plan.period}
                  </span>
                  {plan.monthlyEquiv && (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                        {plan.monthlyEquiv}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: '#10b98122', color: '#10b981' }}>
                        {plan.savings}
                      </span>
                    </div>
                  )}
                </div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {plan.featureKeys.map((featKey) => (
                    <li key={featKey} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--theme-text)' }}>
                      <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: plan.accent }} />
                      {t(featKey)}
                    </li>
                  ))}
                </ul>
                {isCurrentPlan ? (
                  <Button
                    disabled
                    className="w-full rounded-xl py-5 font-medium opacity-60"
                    style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-text)' }}
                    data-testid={`plan-current-${plan.key}`}
                  >
                    {t('plans.current_plan')}
                  </Button>
                ) : plan.key === 'free' && isPaid ? (
                  // Downgrade to Free — cancel the subscription
                  ((appleBillingReady || playBillingReady) ? (<Button
                    onClick={() => {
                      if (!window.confirm(t('plans.confirm_cancel', 'Are you sure you want to cancel your subscription? You\'ll keep access until the end of your billing period.'))) return;
                      const platform = detectedPlatform;
                      if (platform === PLATFORM.ANDROID) {
                        window.open('https://play.google.com/store/account/subscriptions', '_system');
                      } else {
                        // itms-apps:// opens the App Store subscription management natively
                        window.location.href = 'itms-apps://apps.apple.com/account/subscriptions';
                      }
                    }}
                    className="w-full rounded-xl py-5 font-medium gap-2"
                    style={{ background: 'transparent', color: 'var(--theme-text)', border: '1.5px solid var(--glass-border)' }}
                    data-testid="plan-downgrade-free-native"
                  >
                    <ArrowDownRight className="h-4 w-4" />
                    {t('plans.manage_subscription', 'Manage Subscription')}
                  </Button>) : (<Button
                    onClick={async () => {
                      if (!window.confirm(t('plans.confirm_cancel', 'Are you sure you want to cancel your subscription? You\'ll keep access until the end of your billing period.'))) return;
                      try {
                        setLoadingPlan('free');
                        await axios.post(`${API}/payments/cancel`, {}, { withCredentials: true });
                        toast.success(t('plans.cancelled_success', 'Subscription cancelled'));
                        await refreshStatus();
                      } catch (err) {
                        toast.error(t('plans.cancel_error', 'Could not cancel subscription'));
                      } finally {
                        setLoadingPlan(null);
                      }
                    }}
                    disabled={!!loadingPlan}
                    className="w-full rounded-xl py-5 font-medium gap-2"
                    style={{ background: 'transparent', color: 'var(--theme-text)', border: '1.5px solid var(--glass-border)' }}
                    data-testid="plan-downgrade-free-stripe"
                  >
                    {loadingPlan === 'free' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4" />
                    )}
                    {t('plans.downgrade')}
                  </Button>))
                ) : plan.key === 'free' && !isPaid ? (
                  <Button
                    disabled
                    className="w-full rounded-xl py-5 font-medium opacity-60"
                    style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-text)' }}
                    data-testid={`plan-current-free`}
                  >
                    {t('plans.current_plan')}
                  </Button>
                ) : isPaid ? (
                  (appleBillingReady || playBillingReady) ? (
                    <Button
                      onClick={() => handleSubscribe(plan.key)}
                      disabled={!!loadingPlan}
                      className="w-full rounded-xl py-5 font-medium gap-2"
                      style={{
                        background: isUpgrade ? plan.accent : 'transparent',
                        color: isUpgrade ? '#fff' : 'var(--theme-text)',
                        border: isUpgrade ? 'none' : '1.5px solid var(--glass-border)',
                      }}
                      data-testid={`plan-switch-${plan.key}`}
                    >
                      {loadingPlan === plan.key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isUpgrade ? (
                        <ArrowUpRight className="h-4 w-4" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4" />
                      )}
                      {isUpgrade ? t('plans.upgrade') : t('plans.downgrade')}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handlePreviewChange(plan.key)}
                      disabled={!!previewLoading || !!loadingPlan}
                      className="w-full rounded-xl py-5 font-medium gap-2"
                      style={{
                        background: isUpgrade ? plan.accent : 'transparent',
                        color: isUpgrade ? '#fff' : 'var(--theme-text)',
                        border: isUpgrade ? 'none' : '1.5px solid var(--glass-border)',
                      }}
                      data-testid={`plan-switch-${plan.key}`}
                    >
                      {previewLoading === plan.key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isUpgrade ? (
                        <ArrowUpRight className="h-4 w-4" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4" />
                      )}
                      {isUpgrade ? t('plans.upgrade') : t('plans.downgrade')}
                    </Button>
                  )
                ) : (
                  <Button
                    onClick={() => handleSubscribe(plan.key)}
                    disabled={!!loadingPlan}
                    className="w-full rounded-xl py-5 font-medium gap-2"
                    style={{ background: plan.accent, color: '#fff' }}
                    data-testid={`plan-subscribe-${plan.key}`}
                  >
                    {loadingPlan === plan.key ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {t('plans.subscribe')}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Free tier note */}
        <div className="text-center mt-6 space-y-3">
          <p className="text-xs" style={{ color: 'var(--theme-textSecondary)', opacity: 0.7 }}>
            {isPaid
              ? t('plans.proration_note_paid')
              : t('plans.proration_note_free')}
          </p>

          {/* Restore Purchases — shown on iOS and Android native wrappers */}
          {(detectedPlatform === PLATFORM.IOS || detectedPlatform === PLATFORM.ANDROID) && (
            <Button
              variant="ghost"
              onClick={handleRestorePurchases}
              disabled={restoringPurchases}
              className="mx-auto flex items-center gap-2 text-sm"
              style={{ color: 'var(--theme-textSecondary)' }}
              data-testid="restore-purchases-btn"
            >
              {restoringPurchases ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              {t('plans.restore_purchases', 'Restore Purchases')}
            </Button>
          )}

          {/* Payment platform indicator (debug/support) */}
          <p className="text-[10px] opacity-40" style={{ color: 'var(--theme-textSecondary)' }} data-testid="payment-platform-info">
            {detectedPlatform === PLATFORM.IOS
              ? 'Payment via App Store'
              : detectedPlatform === PLATFORM.ANDROID
                ? 'Payment via Google Play'
                : 'Secure payment via Stripe'}
          </p>

          {/* Auto-renew + Terms/Privacy legal footer (required by App Store Guideline 3.1.2) */}
          <div
            className="pt-5 mt-5 text-[11px] leading-relaxed space-y-2"
            style={{
              color: 'var(--theme-textSecondary)',
              borderTop: '1px solid var(--glass-border)',
              opacity: 0.85,
            }}
            data-testid="subscription-legal-footer"
          >
            <p>
              {t('generated.subscriptionplanspage_subscriptions_auto_renew_8b64')}
              {detectedPlatform === PLATFORM.IOS ? ' or via App Store → Subscriptions.' :
               detectedPlatform === PLATFORM.ANDROID ? ' or via Google Play → Subscriptions.' : '.'}
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href="/terms"
                className="underline hover:opacity-80"
                style={{ color: 'var(--theme-accent)' }}
                data-testid="plans-terms-link"
              >
                {t('generated.subscriptionplanspage_terms_of_use_1e0e')}
              </a>
              <a
                href="/privacy"
                className="underline hover:opacity-80"
                style={{ color: 'var(--theme-accent)' }}
                data-testid="plans-privacy-link"
              >
                {t('generated.subscriptionplanspage_privacy_policy_bb61')}
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Proration Preview Modal */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => !changingPlan && setPreview(null)}
          data-testid="proration-modal-overlay"
        >
          <div
            className="mx-4 w-full max-w-sm rounded-3xl p-6"
            style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="proration-modal"
          >
            <div className="flex items-center justify-center mb-4">
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center"
                style={{ background: preview.is_upgrade ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)' }}
              >
                <RefreshCw className="h-6 w-6" style={{ color: preview.is_upgrade ? '#22c55e' : '#f59e0b' }} />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-center mb-1" style={{ color: 'var(--theme-text)' }}>
              {preview.is_upgrade ? t('plans.upgrade_to', { name: getPlanName(preview.targetPlan) }) : t('plans.downgrade_to', { name: getPlanName(preview.targetPlan) })}
            </h3>
            <p className="text-xs text-center mb-5" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('plans.changes_immediate')}
            </p>

            {/* Proration Breakdown */}
            <div
              className="rounded-2xl p-4 mb-5 space-y-3"
              style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
              data-testid="proration-breakdown"
            >
              {preview.credit_cents > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--theme-textSecondary)' }}>
                    {t('plans.credit_unused', { name: getPlanName(preview.current_plan) })}
                  </span>
                  <span className="font-medium" style={{ color: '#22c55e' }}>
                    -{fmt(preview.credit_cents)}
                  </span>
                </div>
              )}
              {preview.charge_cents > 0 && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--theme-textSecondary)' }}>
                    {t('plans.remaining_days', { name: getPlanName(preview.targetPlan) })}
                  </span>
                  <span className="font-medium" style={{ color: 'var(--theme-text)' }}>
                    {fmt(preview.charge_cents)}
                  </span>
                </div>
              )}
              <div className="border-t pt-2" style={{ borderColor: 'var(--glass-border)' }}>
                <div className="flex justify-between text-sm font-semibold">
                  <span style={{ color: 'var(--theme-text)' }}>
                    {preview.net_cents > 0 ? t('plans.due_today') : preview.net_cents < 0 ? t('plans.credit_applied') : t('plans.no_charge')}
                  </span>
                  <span style={{ color: preview.net_cents > 0 ? 'var(--theme-text)' : '#22c55e' }}>
                    {preview.net_cents > 0 ? fmt(preview.net_cents) : preview.net_cents < 0 ? `-${fmt(preview.net_cents)}` : '$0.00'}
                  </span>
                </div>
              </div>
              <p className="text-[11px] pt-1" style={{ color: 'var(--theme-textSecondary)', opacity: 0.7 }}>
                {t('plans.then_monthly', { price: PLANS.find(p => p.key === preview.targetPlan)?.price })}
              </p>
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleConfirmChange}
                disabled={changingPlan}
                className="w-full rounded-xl py-5 font-medium gap-2"
                style={{ background: preview.is_upgrade ? '#22c55e' : '#f59e0b', color: '#fff' }}
                data-testid="confirm-plan-change-btn"
              >
                {changingPlan ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : preview.is_upgrade ? (
                  <ArrowUpRight className="h-4 w-4" />
                ) : (
                  <ArrowDownRight className="h-4 w-4" />
                )}
                {changingPlan ? t('plans.switching') : `${preview.is_upgrade ? t('plans.confirm_upgrade') : t('plans.confirm_downgrade')}`}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPreview(null)}
                disabled={changingPlan}
                className="w-full rounded-xl py-4 text-sm"
                style={{ color: 'var(--theme-textSecondary)' }}
                data-testid="cancel-plan-change-btn"
              >
                {t('plans.keep_current')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
